import { useState, useEffect } from 'react';
import { Search } from '@mui/icons-material';
import {
  TextField,
  Popover,
  Typography,
  Box,
  CircularProgress,
  styled,
} from '@mui/material';
import { Column, Row } from 'components/Layout';
import SearchResults from './SearchResults';
import LatestSearched from './LatestSearched';
import { useLayoutContext } from 'contexts/LayoutContext';
import useGlobalSearch, { GlobalSearchState } from './useGlobalSearch';

const SearchHandle = styled(Row)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xxs, 3),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: '16px',
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down(theme.sidebar.drawerBreakpoint)]: {
    display: 'none',
  },
  cursor: 'text',
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.grey[500],
  fontSize: theme.fontSizes.regular,
  lineHeight: '16px',
}));

const InnerSearchField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'width' && prop !== 'resultsVisible',
})<{
  width?: number;
  resultsVisible?: boolean;
}>(({ theme, width, resultsVisible }) => ({
  width: width ?? 'auto',

  '& .MuiInputBase-root': {
    padding: theme.spacing(0, 3),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },

  '& .MuiInputBase-input': {
    padding: theme.spacing('6px', 0),
    height: '20px',
    lineHeight: '20px',
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
    borderBottom: resultsVisible
      ? `1px solid ${theme.palette.grey[300]}`
      : undefined,
  },

  '& .MuiInputBase-root.Mui-focused': {
    borderColor: theme.palette.grey[300],
  },
}));

const SearchModal = styled(Popover)(({ theme }) => ({
  '& .MuiPopover-paper': {
    borderRadius: '16px',
    border: `1px solid ${theme.palette.grey[300]}`,
    boxShadow: 'none',
  },
}));

const Container = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'width',
})<{ width?: number }>(({ theme, width }) => ({
  padding: theme.spacing(
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
    theme.spacingSizes.lg,
    theme.spacingSizes.sm,
  ),
  width: width ?? 'auto',
}));

const ResultsHeading = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(0, theme.spacingSizes.xs),
}));

const GlobalSearch: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { selectedVehicleId } = useLayoutContext();
  const [width, setWidth] = useState<number>(0);

  const { state, query, debouncedQuery, setQuery, latestSearched, results } =
    useGlobalSearch(selectedVehicleId);

  useEffect(() => {
    if (anchorEl) {
      setWidth(anchorEl.offsetWidth);
    }

    const handleResize = () => {
      setWidth(anchorEl?.offsetWidth ?? 0);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [anchorEl]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isOpen = Boolean(anchorEl);

  const heading =
    state === GlobalSearchState.LatestSearched
      ? 'Latest searched'
      : 'Search result(s)';

  return (
    <Box>
      <SearchHandle
        fullWidth
        centered
        spacing="xs"
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
        }}
      >
        <Search /> <span>Search</span>
      </SearchHandle>
      <SearchModal
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        marginThreshold={null}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <InnerSearchField
          fullWidth
          autoFocus
          value={query}
          variant="outlined"
          size="small"
          helperText={false}
          placeholder="Search"
          slotProps={{
            input: {
              startAdornment: <Search />,
            },
          }}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          width={width}
          resultsVisible={state !== GlobalSearchState.Empty}
        />
        {state !== GlobalSearchState.Empty && (
          <Container spacing="sm" width={width}>
            <ResultsHeading variant="body2">{heading}</ResultsHeading>
            {state === GlobalSearchState.Loading && (
              <Row fullWidth justify="center">
                <CircularProgress size={20} />
              </Row>
            )}
            {state === GlobalSearchState.Results && !!results && (
              <SearchResults
                searchQuery={debouncedQuery}
                results={results}
                vehicleId={selectedVehicleId ?? ''}
              />
            )}
            {state === GlobalSearchState.LatestSearched && (
              <LatestSearched latestSearched={latestSearched} />
            )}
          </Container>
        )}
      </SearchModal>
    </Box>
  );
};

export default GlobalSearch;
